import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Shield,
  AlertTriangle,
  Lock,
  Unlock,
  Search,
  Activity,
  Clock,
  Globe,
  CheckCircle,
  XCircle,
  RefreshCw,
  Wifi,
  WifiOff,
} from 'lucide-react';
import backendApi from '../../services/backendApi';

interface LoginAttempt {
  id: number;
  username: string;
  ip_address: string;
  success: boolean;
  attempted_at: string;
  user?: {
    id: number;
    username: string;
    email?: string;
  };
  site?: {
    site_name: string;
  };
}

interface LockedAccount {
  id: number;
  username: string;
  email?: string;
  account_locked_until?: string;
  locked_reason?: string;
  failed_login_attempts?: number;
}

interface SuspiciousIP {
  ip_address?: string;
  ip?: string;
  attempt_count?: number;
  count?: number;
}

const SecurityMonitoring: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [isRealTime, setIsRealTime] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');

  const [securityStats, setSecurityStats] = useState({
    total_attempts: 0,
    successful_logins: 0,
    failed_logins: 0,
    attempts_today: 0,
    failed_today: 0,
    attempts_last_7_days: 0,
    unique_ips_last_24h: 0,
    locked_accounts: 0,
  });

  const [lockedAccounts, setLockedAccounts] = useState<LockedAccount[]>([]);
  const [loginAttempts, setLoginAttempts] = useState<LoginAttempt[]>([]);
  const [suspiciousIPs, setSuspiciousIPs] = useState<SuspiciousIP[]>([]);

  const pollInterval = 30000; // 30s scalable polling (prevents DB connection exhaustion)

  useEffect(() => {
    void fetchAllSecurityData(true);
  }, []);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    let isMounted = true;
    let currentInterval = pollInterval;
    let consecutiveErrors = 0;

    const pollData = async () => {
      if (!isMounted || !isRealTime) return;

      try {
        const token = sessionStorage.getItem('access_token');
        if (!token) {
          consecutiveErrors++;
          currentInterval = Math.min(pollInterval * Math.pow(2, consecutiveErrors), 60000);
          if (isMounted) timeoutId = setTimeout(pollData, currentInterval);
          return;
        }

        await fetchAllSecurityData(false);
        consecutiveErrors = 0;
        currentInterval = pollInterval;

        if (isMounted && isRealTime) {
          timeoutId = setTimeout(pollData, currentInterval);
        }
      } catch (error: any) {
        if (error?.message?.includes('401')) {
          consecutiveErrors++;
          currentInterval = Math.min(pollInterval * Math.pow(2, consecutiveErrors), 60000);
        } else {
          currentInterval = Math.min(currentInterval + 5000, 30000);
        }
        if (isMounted && isRealTime) {
          timeoutId = setTimeout(pollData, currentInterval);
        }
      }
    };

    if (isRealTime) {
      timeoutId = setTimeout(() => {
        if (isMounted) pollData();
      }, currentInterval);
    }

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isRealTime]);

  const fetchAllSecurityData = async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      } else {
        setIsUpdating(true);
      }

      const [statsRes, lockedRes, attemptsRes, suspiciousRes] = await Promise.all([
        backendApi.getSecurityStats(),
        backendApi.getLockedAccounts(),
        backendApi.getLoginAttempts(50),
        backendApi.getSuspiciousIPs(24, 5),
      ]);

      if (statsRes.success) {
        const statsData = statsRes.data || statsRes;
        setSecurityStats({
          total_attempts: statsData.total_attempts || 0,
          successful_logins: statsData.successful_logins || 0,
          failed_logins: statsData.failed_logins || 0,
          attempts_today: statsData.attempts_today || 0,
          failed_today: statsData.failed_today || 0,
          attempts_last_7_days: statsData.attempts_last_7_days || 0,
          unique_ips_last_24h: statsData.unique_ips_last_24h || 0,
          locked_accounts: statsData.locked_accounts || 0,
        });
      }

      if (lockedRes.success) {
        const lockedData = lockedRes.data?.locked_accounts || lockedRes.locked_accounts || [];
        setLockedAccounts(Array.isArray(lockedData) ? lockedData : []);
      }

      if (attemptsRes.success) {
        const attemptsData = attemptsRes.data?.login_attempts || attemptsRes.login_attempts || [];
        setLoginAttempts(Array.isArray(attemptsData) ? attemptsData : []);
      }

      if (suspiciousRes.success) {
        const suspiciousData = suspiciousRes.data?.suspicious_ips || suspiciousRes.suspicious_ips || [];
        setSuspiciousIPs(Array.isArray(suspiciousData) ? suspiciousData : []);
      }

      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error fetching security data:', error);
    } finally {
      if (showLoading) {
        setLoading(false);
      } else {
        setIsUpdating(false);
      }
    }
  };

  const toggleRealTime = () => {
    setIsRealTime((value) => {
      const newValue = !value;
      if (newValue) {
        void fetchAllSecurityData(false);
      }
      return newValue;
    });
  };

  const handleUnlock = async (userId: number) => {
    if (!window.confirm('Are you sure you want to unlock this account?')) {
      return;
    }

    try {
      const response = await backendApi.unlockUser(userId);
      if (response.success) {
        alert('Account unlocked successfully');
        void fetchAllSecurityData(false);
      } else {
        alert(response.error || 'Failed to unlock account');
      }
    } catch (error: any) {
      alert(error.message || 'Failed to unlock account');
    }
  };

  const filteredAttempts = useMemo(() => {
    return loginAttempts.filter((attempt) => {
      const matchesSearch =
        attempt.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        attempt.ip_address?.toLowerCase().includes(searchTerm.toLowerCase());

      if (!matchesSearch) {
        return false;
      }

      if (dateFilter === 'today') {
        const attemptDate = new Date(attempt.attempted_at);
        const today = new Date();
        return attemptDate.toDateString() === today.toDateString();
      }

      if (dateFilter === 'week') {
        const attemptDate = new Date(attempt.attempted_at);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return attemptDate >= weekAgo;
      }

      return true;
    });
  }, [dateFilter, loginAttempts, searchTerm]);

  const filteredLockedAccounts = useMemo(() => {
    return lockedAccounts.filter((account) => {
      const term = searchTerm.toLowerCase();
      return (
        !term ||
        account.username?.toLowerCase().includes(term) ||
        account.email?.toLowerCase().includes(term)
      );
    });
  }, [lockedAccounts, searchTerm]);

  const filteredSuspiciousIPs = useMemo(() => {
    return suspiciousIPs.filter((ip) => {
      const value = String(ip.ip_address || ip.ip || '').toLowerCase();
      return !searchTerm || value.includes(searchTerm.toLowerCase());
    });
  }, [searchTerm, suspiciousIPs]);

  const securityAlertActive = securityStats.locked_accounts > 0 || securityStats.failed_today > 10;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center admin-shell">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0084CF]/30 border-t-[#0084CF] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading security monitoring...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 lg:pt-4 pb-8 px-2 sm:px-3 lg:px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        <motion.section initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="admin-hero p-4 sm:p-4 lg:p-5">
          <div className="admin-toolbar">
            <div className="space-y-2">
              <p className="admin-eyebrow">Security monitoring</p>
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex w-11 h-11 rounded-[16px] bg-[linear-gradient(135deg,#0084CF,#1C4D88)] items-center justify-center text-white shadow-[0_12px_20px_rgba(0,132,207,0.2)]">
                  <Shield size={22} />
                </div>
                <div>
                  <h1 className="admin-page-title">Threat signals, account lockouts, and login flow in one operational view.</h1>
                  <p className="admin-page-subtitle mt-2">The page now follows the same calmer admin language, with searchable security events and action-ready rows instead of disconnected cards.</p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <span className={`admin-pill ${securityAlertActive ? 'admin-pill-danger' : 'admin-pill-success'}`}>
                      <AlertTriangle size={12} />
                      {securityAlertActive ? 'Action required' : 'System stable'}
                    </span>
                    <span className="admin-pill admin-pill-muted">
                      <Clock size={12} />
                      {lastUpdate ? `Updated ${lastUpdate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'No sync yet'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button onClick={toggleRealTime} className={`admin-button ${isRealTime ? 'admin-button-secondary' : 'admin-button-ghost'}`} title={isRealTime ? 'Disable real-time updates' : 'Enable real-time updates'}>
                {isRealTime ? <Wifi size={16} /> : <WifiOff size={16} />}
                {isRealTime ? 'Live sync' : 'Paused'}
              </button>
              <button onClick={() => fetchAllSecurityData(true)} className="admin-button admin-button-primary" title="Refresh security data" aria-label="Refresh security data">
                <RefreshCw size={16} className={isUpdating ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>
          </div>

          <div className="admin-stat-strip mt-6">
            <div className="admin-stat-card">
              <p className="admin-stat-label">Total attempts</p>
              <p className="admin-stat-value">{securityStats.total_attempts}</p>
              <p className="admin-stat-hint">All recorded login attempts</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Failed</p>
              <p className="admin-stat-value">{securityStats.failed_logins}</p>
              <p className="admin-stat-hint">{securityStats.failed_today} failed attempts today</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Locked</p>
              <p className="admin-stat-value">{securityStats.locked_accounts}</p>
              <p className="admin-stat-hint">Accounts awaiting unlock action</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Unique IPs</p>
              <p className="admin-stat-value">{securityStats.unique_ips_last_24h}</p>
              <p className="admin-stat-hint">Distinct IP addresses in the last 24 hours</p>
            </div>
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="admin-section">
          <div className="admin-section-header">
            <div>
              <h2 className="admin-section-title">Filter security events</h2>
              <p className="admin-section-copy">Search by username or IP and narrow recent activity windows.</p>
            </div>
          </div>
          <div className="admin-section-body">
            <div className="admin-filter-grid" style={{ gridTemplateColumns: 'minmax(0,1.6fr) minmax(180px,0.45fr)' }}>
              <div className="admin-search-wrap">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search by username or IP address"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="admin-input"
                />
              </div>
              <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="admin-select">
                <option value="all">All time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 days</option>
              </select>
            </div>
          </div>
        </motion.section>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="admin-section">
            <div className="admin-section-header">
              <div>
                <h2 className="admin-section-title">Locked accounts</h2>
                <p className="admin-section-copy">Accounts currently blocked because of failed login or security policy triggers.</p>
              </div>
              <span className="admin-pill admin-pill-muted">{filteredLockedAccounts.length} accounts</span>
            </div>
            <div className="admin-section-body">
              {filteredLockedAccounts.length === 0 ? (
                <div className="admin-empty">No locked accounts match the current filters.</div>
              ) : (
                <div className="admin-row-list max-h-[560px] overflow-y-auto pr-1">
                  {filteredLockedAccounts.map((account, index) => (
                    <motion.div key={account.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index, 8) * 0.04 }} className="admin-row">
                      <div className="admin-row-leading">
                        <div className="admin-row-icon"><Lock size={18} /></div>
                        <div className="min-w-0">
                          <p className="admin-row-title truncate">{account.username || 'Unknown account'}</p>
                          <p className="admin-row-subtitle truncate">{account.email || 'No email on file'}</p>
                          <div className="admin-row-tags">
                            <span className="admin-tag">{account.failed_login_attempts || 0} failed attempts</span>
                          </div>
                        </div>
                      </div>
                      <div className="admin-row-meta">
                        <div className="admin-meta-block">
                          <p className="admin-meta-label">Reason</p>
                          <p className="admin-meta-value">{account.locked_reason || 'Failed login attempts'}</p>
                        </div>
                        <div className="admin-meta-block">
                          <p className="admin-meta-label">Locked until</p>
                          <p className="admin-meta-value">{account.account_locked_until ? new Date(account.account_locked_until).toLocaleString() : 'Manual unlock required'}</p>
                        </div>
                      </div>
                      <div className="admin-row-actions">
                        <span className="admin-pill admin-pill-danger">Locked</span>
                        <button onClick={() => handleUnlock(account.id)} className="admin-button admin-button-primary">
                          <Unlock size={14} />
                          Unlock
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.14 }} className="admin-section">
            <div className="admin-section-header">
              <div>
                <h2 className="admin-section-title">Recent login attempts</h2>
                <p className="admin-section-copy">Authentication attempts are now arranged as action-oriented rows for faster scanning.</p>
              </div>
              {isRealTime && <span className="admin-pill admin-pill-success">Live</span>}
            </div>
            <div className="admin-section-body">
              {filteredAttempts.length === 0 ? (
                <div className="admin-empty">No login attempts match the current filters.</div>
              ) : (
                <div className="admin-row-list max-h-[560px] overflow-y-auto pr-1">
                  {filteredAttempts.map((attempt, index) => (
                    <motion.div key={attempt.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index, 8) * 0.025 }} className="admin-row">
                      <div className="admin-row-leading">
                        <div className="admin-row-icon">
                          {attempt.success ? <CheckCircle size={18} /> : <XCircle size={18} />}
                        </div>
                        <div className="min-w-0">
                          <p className="admin-row-title truncate">{attempt.username || 'Unknown user'}</p>
                          <p className="admin-row-subtitle truncate">{attempt.site?.site_name || 'No site context'} • {new Date(attempt.attempted_at).toLocaleString()}</p>
                          <div className="admin-row-tags">
                            <span className="admin-tag">{attempt.ip_address || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="admin-row-meta">
                        <div className="admin-meta-block">
                          <p className="admin-meta-label">Result</p>
                          <p className="admin-meta-value">{attempt.success ? 'Successful login' : 'Failed login'}</p>
                        </div>
                        <div className="admin-meta-block">
                          <p className="admin-meta-label">Window</p>
                          <p className="admin-meta-value">{dateFilter === 'all' ? 'Complete history' : dateFilter === 'today' ? 'Today only' : '7 day window'}</p>
                        </div>
                      </div>
                      <div className="admin-row-actions">
                        <span className={`admin-pill ${attempt.success ? 'admin-pill-success' : 'admin-pill-danger'}`}>
                          {attempt.success ? 'Success' : 'Failed'}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.section>
        </div>

        {filteredSuspiciousIPs.length > 0 && (
          <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }} className="admin-section">
            <div className="admin-section-header">
              <div>
                <h2 className="admin-section-title">Suspicious IP addresses</h2>
                <p className="admin-section-copy">Repeated failed attempts are highlighted here as row entries instead of isolated alert cards.</p>
              </div>
            </div>
            <div className="admin-section-body">
              <div className="admin-row-list">
                {filteredSuspiciousIPs.map((ip, index) => (
                  <motion.div key={`${ip.ip_address || ip.ip || 'unknown'}-${index}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index, 8) * 0.04 }} className="admin-row">
                    <div className="admin-row-leading">
                      <div className="admin-row-icon"><Globe size={18} /></div>
                      <div className="min-w-0">
                        <p className="admin-row-title truncate">{ip.ip_address || ip.ip || 'Unknown IP'}</p>
                        <p className="admin-row-subtitle">Repeated failed login attempts detected from the same network source.</p>
                      </div>
                    </div>
                    <div className="admin-row-meta">
                      <div className="admin-meta-block">
                        <p className="admin-meta-label">Failed attempts</p>
                        <p className="admin-meta-value">{ip.attempt_count || ip.count || 0}</p>
                      </div>
                      <div className="admin-meta-block">
                        <p className="admin-meta-label">Classification</p>
                        <p className="admin-meta-value">Security anomaly</p>
                      </div>
                    </div>
                    <div className="admin-row-actions">
                      <span className="admin-pill admin-pill-danger">Investigate</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.section>
        )}

        <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 }} className="admin-section">
          <div className="admin-section-header">
            <div>
              <h2 className="admin-section-title">Security status</h2>
              <p className="admin-section-copy">A plain-language summary of the current security posture for this admin context.</p>
            </div>
          </div>
          <div className="admin-section-body">
            <div className={`rounded-[20px] p-5 border ${securityAlertActive ? 'bg-[rgba(181,71,93,0.08)] border-[rgba(181,71,93,0.18)]' : 'bg-[rgba(20,134,109,0.08)] border-[rgba(20,134,109,0.18)]'}`}>
              <p className={`text-sm leading-7 ${securityAlertActive ? 'text-[#8C2F45]' : 'text-[#0F6C5A]'}`}>
                <strong>{securityAlertActive ? 'Active security alerts detected.' : 'System is operating normally.'}</strong>{' '}
                {securityStats.locked_accounts > 0 && `${securityStats.locked_accounts} account(s) are currently locked. `}
                {securityStats.failed_today > 10 && `${securityStats.failed_today} failed login attempts were recorded today. `}
                {!securityAlertActive && 'No immediate lockout or spike conditions are being reported.'}
              </p>
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default SecurityMonitoring;